import Token from 'common/models/MyToken';
import { Nullable, Optional } from 'common/models/optional';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';

export namespace NumberUtils {
  export function formatFloatStandard(
    n: number | string | undefined,
    trunc = true
  ): Optional<string> {
    if (n == null || isNaN(n)) {
      // TODO: Consider adding a loading for anything that calls this
      return '0';
    }
    let theNumber = +n;
    if (trunc) {
      theNumber = Math.trunc(theNumber);
    }
    return theNumber.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  export function formatFloatExtended(
    n: number | string | undefined,
    displayDecimals = 2
  ): Optional<string> {
    if (n == null || isNaN(n)) {
      // TODO: Consider adding a loading for anything that calls this
      return '0';
    }
    const theNumber = +n;

    return theNumber.toLocaleString(undefined, {
      maximumFractionDigits: displayDecimals,
    });
  }

  export function determineDecimals(n: number | string | undefined): number {
    if (n == null || isNaN(n)) {
      return 2;
    }

    if (Number(n) > 0 && Number(n) < 0.01) {
      return 4;
    }

    return 2;
  }

  // converts BN string to a number
  export function BNtoNumberWithoutDecimals(
    val: string,
    decimals: number
  ): number {
    if (val != null) {
      const digits = val.length;
      let tempVal = '';
      if (digits <= decimals) {
        tempVal = '0.';
        for (let i = 0; i < decimals - digits; i++) {
          tempVal = `${tempVal}0`;
        }
        tempVal = `${tempVal}${val}`;
      } else {
        for (let i = 0; i < digits - decimals; i++) {
          tempVal = `${tempVal}${val[i]}`;
        }
        tempVal = `${tempVal}.`;
        for (let i = digits - decimals; i < digits; i++) {
          tempVal = `${tempVal}${val[i]}`;
        }
      }
      return Number(tempVal);
    }
    return 0;
  }

  // converts BN to a user input string with a decimal point
  export function BNtoStringWithoutDecimals(
    value: bigint,
    decimals: number
  ): string {
    if (value != null && value > 0n) {
      const val = value.toString();
      const digits = val.length;
      let tempVal = '';
      if (digits <= decimals) {
        tempVal = '0.';
        for (let i = 0; i < decimals - digits; i++) {
          tempVal = `${tempVal}0`;
        }
        tempVal = `${tempVal}${val}`;
      } else {
        for (let i = 0; i < digits - decimals; i++) {
          tempVal = `${tempVal}${val[i]}`;
        }
        tempVal = `${tempVal}.`;
        for (let i = digits - decimals; i < digits; i++) {
          tempVal = `${tempVal}${val[i]}`;
        }
      }
      tempVal = tempVal.replace(/0+$/, '');
      tempVal = tempVal.replace(/\.$/, '');
      if (tempVal === '') {
        tempVal = '0';
      }
      return tempVal;
    }
    return '0';
  }

  export function formatBigNumber(
    value: bigint,
    token: Token | null | undefined,
    decimals?: number
  ): number {
    if (value == null) {
      throw new Error('Cannot format a null or undefined value');
    }
    if (token == null) {
      throw new Error('Cannot format a BigNumber without a Token');
    }
    if (token.decimals == null) {
      throw new Error(
        'Cannot format a BigNumber without a Token decimals configured'
      );
    }
    const decimalOverride =
      decimals && isNumber(decimals) ? decimals : token.decimals;
    const n = BNtoNumberWithoutDecimals(value.toString(), decimalOverride);
    return n;
  }

  // converts user input amount (with decimal point) to BN string with specified decimals
  export function stringToBNString(val: string, decimals = 0n): string {
    if (val != null) {
      const dotIndex = val.indexOf('.');
      let numDecimals = 0n;
      if (dotIndex >= 0) {
        numDecimals = BigInt(val.length - dotIndex - 1);
      }
      let tempVal = val.replace('.', '');
      if (numDecimals > decimals) {
        tempVal = tempVal.substring(
          0,
          tempVal.length - Number(numDecimals - decimals)
        );
      }
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < decimals - numDecimals; i++) {
        tempVal = `${tempVal}0`;
      }
      tempVal = tempVal.replace(/^0+/, '');
      if (tempVal.length === 0) {
        tempVal = '0';
      }
      return tempVal;
    }
    return '0';
  }

  // converts user input amount (with decimal point) to BN with specified decimals
  export function stringToBN(val: string, decimals?: bigint): bigint {
    try {
      return BigInt(stringToBNString(val, decimals));
    } catch (error) {
      return 0n;
    }
  }

  export function addDecimalPointToString(
    val: string,
    decimals: number
  ): string {
    let tempVal = val;
    while (tempVal.length <= decimals) {
      tempVal = `0${tempVal}`;
    }
    tempVal = `${tempVal.substring(
      0,
      tempVal.length - decimals
    )}.${tempVal.substring(tempVal.length - decimals)}`;
    tempVal = tempVal.replace(/0+$/, '');
    if (tempVal[tempVal.length - 1] === '.') {
      tempVal = tempVal.substring(0, tempVal.length - 1);
    }
    return tempVal;
  }

  // do not use this function on user input strings, use stringToBNString instead
  // ok to use it for values calculated (for instance for required amounts validation)
  export function getBNString18(val: number): string {
    if (val >= 100000) {
      return `${Math.floor(val * 10 ** 9)}000000000`;
      // eslint-disable-next-line no-else-return
    } else if (val >= 1000) {
      return `${Math.floor(val * 10 ** 12)}000000`;
    }
    return `${Math.floor(val * 10 ** 18)}`;
  }

  // do not use this function on user input strings, use stringToBNString instead
  // ok to use it for values calculated (for instance for required amounts validation)
  export function getBNString9(val: number): string {
    return `${Math.floor(val * 10 ** 9)}`;
  }

  export function threeDecimals(n: number) {
    const res = n.toFixed(20).match(/^-?\d*\.?0*\d{0,3}/);
    if (res && res[0]) {
      return Number(res[0]).toString();
    }
    return Number(n).toString();
  }

  export function significantCeil(val: string, decimals: number) {
    const exactValue = Number(val) / 10 ** decimals;
    let res = Math.ceil(exactValue * 1000) / 1000;
    if (exactValue > 1) {
      res = Math.ceil(exactValue * 1000) / 1000;
    } else if (exactValue > 0.1) {
      res = Math.ceil(exactValue * 10000) / 10000;
    } else if (exactValue > 0.01) {
      res = Math.ceil(exactValue * 100000) / 100000;
    } else if (exactValue > 0.001) {
      res = Math.ceil(exactValue * 1000000) / 1000000;
    } else if (exactValue > 0.0001) {
      res = Math.ceil(exactValue * 10000000) / 10000000;
    } else if (exactValue > 0.00001) {
      res = Math.ceil(exactValue * 100000000) / 100000000;
    }
    return res;
  }

  // NOTE that currently all decimals are 18 currently, but that could be different in the future with HODL vaults
  // Where percentage is a whole number like 25, 50, 75, 100
  export function amountBasedOnPercentage(
    val: Optional<bigint>,
    percentage: number,
    decimals = 18
  ): string {
    if (!val) {
      console.warn(
        'Could not calculate amount based on percentage as amount was not defined'
      );
      return '0';
    }
    const tokenBalance = val ?? 0n;
    const amount = NumberUtils.BNtoStringWithoutDecimals(
      (tokenBalance * BigInt(percentage)) / 100n,
      decimals
    );
    return amount;
  }

  export function percentageBasedOnAmount(
    amount: string,
    val: Optional<bigint>,
    decimals = 18
  ): number {
    if (!val || val === 0n) {
      console.warn('Could not calculate percentage as value was not defined');
      return 0;
    }
    if (!amount) {
      console.warn('Could not calculate percentage as amount was not defined');
      return 0;
    }
    // const tokenBalance = val ?? BigNumber.from(amount);
    const tokenBalance =
      BNtoNumberWithoutDecimals(val.toString(), decimals) ?? amount;
    const amountNum = Number(amount) ?? 0;
    const percentage = (amountNum / tokenBalance) * 100;
    return percentage;
  }

  export function lpToPercentageAmount(
    lp: Optional<bigint>,
    percentage: number,
    decimals = 18
  ): string {
    const bnPercentage = BigInt((percentage * 10 ** 9).toFixed(0));
    if (!lp) {
      return '0';
    }
    const amount = NumberUtils.BNtoStringWithoutDecimals(
      (lp * bnPercentage) / BigInt(10 ** 11),
      decimals
    );
    return amount;
  }

  export function lpToPercentageAmountUsd(
    lp: Optional<bigint>,
    lpPrice: number,
    percentage: number,
    decimals = 18
  ): number {
    if (!lp) {
      return 0;
    }

    const amount = lpToPercentageAmount(lp, percentage, decimals);
    const amountUsd = Number(amount) * lpPrice;
    return amountUsd;
  }

  export function lpUsdToPercentageNumber(
    lpUsd: Nullable<number>,
    percentage: number
  ): number {
    if (!lpUsd) {
      return 0;
    }
    return (lpUsd * percentage) / 100;
  }

  export function lpToAmountBasedOnPercentage(
    lp: Optional<string>,
    percentage: number,
    decimals = 18n
  ): string {
    if (!lp) {
      return '0';
    }

    const lpBNString = NumberUtils.stringToBNString(lp, decimals);
    return NumberUtils.amountBasedOnPercentage(BigInt(lpBNString), percentage);
  }

  export function lpToUsdPercentageNumber(
    lp: Optional<string>,
    lpPrice: Optional<number>,
    percentage: number
  ): number {
    if (!lp || !lpPrice) {
      return 0;
    }
    return (Number(lp) * lpPrice * percentage) / 100;
  }
}
